export const EMPTY_EQUIPE2_TECH_FILTERS = {
  coordenadores: [],
  areas: [],
  tecnicos: [],
  cargos: [],
  fabricantes: [],
  tecnologias: [],
  showInactive: false,
};

export function clearEquipe2TechFilters() {
  return {
    coordenadores: [],
    areas: [],
    tecnicos: [],
    cargos: [],
    fabricantes: [],
    tecnologias: [],
    showInactive: false,
  };
}

export function isTechInactive(tech) {
  return String(tech?.situacao ?? '').trim().toUpperCase() !== 'A';
}

export function countEquipe2TechFilters(filters) {
  if (!filters) return 0;
  return (
    (filters.coordenadores?.length || 0)
    + (filters.areas?.length || 0)
    + (filters.tecnicos?.length || 0)
    + (filters.cargos?.length || 0)
    + (filters.fabricantes?.length || 0)
    + (filters.tecnologias?.length || 0)
  );
}

export function hasEquipe2TechLevelFilter(filters) {
  if (!filters) return false;
  return (
    (filters.tecnicos?.length || 0) > 0
    || (filters.cargos?.length || 0) > 0
    || (filters.fabricantes?.length || 0) > 0
    || (filters.tecnologias?.length || 0) > 0
  );
}

function normalizeEspFilterIds(v) {
  if (v == null || v === '') return [];
  if (Array.isArray(v)) return v.map(String).filter(Boolean);
  return [String(v)];
}

/** Mesma regra da Gestão de técnicos (LCMY_REG_MODELS_TEC). */
export function techMatchesEspFilter(codigo, marcaIds, tecnologiaIds, links) {
  const m = normalizeEspFilterIds(marcaIds);
  const t = normalizeEspFilterIds(tecnologiaIds);
  if (!m.length && !t.length) return true;
  const c = String(codigo || '').trim();
  const rows = (links || []).filter((l) => String(l.codigo || '').trim() === c);
  if (!rows.length) return false;
  if (m.length && t.length) {
    return rows.some((r) => m.includes(String(r.idBrand)) && t.includes(String(r.idSubgroup)));
  }
  if (m.length) return rows.some((r) => m.includes(String(r.idBrand)));
  return rows.some((r) => t.includes(String(r.idSubgroup)));
}

export function buildGestaoByCodigo(gestaoList) {
  const map = new Map();
  for (const row of gestaoList || []) {
    map.set(String(row.codigo), row);
  }
  return map;
}

export function cargoLabelFromGestao(gestaoRow) {
  const c = gestaoRow?.cargo && String(gestaoRow.cargo).trim();
  return c || 'Sem cargo';
}

export function techMatchesGestaoAttributeFilters(techId, filters, gestaoByCodigo, espLinks) {
  const cargos = filters?.cargos || [];
  const fabricantes = filters?.fabricantes || [];
  const tecnologias = filters?.tecnologias || [];
  if (!cargos.length && !fabricantes.length && !tecnologias.length) return true;

  const gestao = gestaoByCodigo?.get(String(techId));

  if (cargos.length) {
    const label = cargoLabelFromGestao(gestao);
    if (!cargos.includes(label)) return false;
  }

  if (fabricantes.length || tecnologias.length) {
    if (!gestao?.gestaoHabilitado) return false;
    return techMatchesEspFilter(
      techId,
      fabricantes.length ? fabricantes : null,
      tecnologias.length ? tecnologias : null,
      espLinks,
    );
  }

  return true;
}

export function filterTecnicosList(tecnicos, filters, gestaoByCodigo, espLinks) {
  const selTecnicos = filters?.tecnicos || [];
  return (tecnicos || []).filter((tech) => {
    if (selTecnicos.length && !selTecnicos.includes(tech.nome)) return false;
    return techMatchesGestaoAttributeFilters(tech.id, filters, gestaoByCodigo, espLinks);
  });
}

export function buildCargoFilterOptions(gestaoList) {
  const set = new Set();
  for (const row of gestaoList || []) {
    set.add(cargoLabelFromGestao(row));
  }
  return [...set]
    .sort((a, b) => a.localeCompare(b, 'pt-BR', { sensitivity: 'base' }))
    .map((label) => ({ value: label, label }));
}

export function buildMarcaFilterOptions(marcas, tecnologiaIds, links) {
  if (!tecnologiaIds?.length) {
    return (marcas || []).map((m) => ({ value: String(m.id), label: m.nome || String(m.id) }));
  }
  const tSet = new Set(tecnologiaIds.map(String));
  const brandSet = new Set();
  for (const link of links || []) {
    if (tSet.has(String(link.idSubgroup))) brandSet.add(String(link.idBrand));
  }
  return (marcas || [])
    .filter((m) => brandSet.has(String(m.id)))
    .map((m) => ({ value: String(m.id), label: m.nome || String(m.id) }));
}

export function buildTecnologiaFilterOptions(tecnologias, fabricanteIds, links) {
  if (!fabricanteIds?.length) {
    return (tecnologias || []).map((t) => ({ value: String(t.id), label: t.nome || String(t.id) }));
  }
  const mSet = new Set(fabricanteIds.map(String));
  const subSet = new Set();
  for (const link of links || []) {
    if (mSet.has(String(link.idBrand))) subSet.add(String(link.idSubgroup));
  }
  return (tecnologias || [])
    .filter((t) => subSet.has(String(t.id)))
    .map((t) => ({ value: String(t.id), label: t.nome || String(t.id) }));
}

export function areaMatchesStructFilters(area, filters, coordenadores) {
  const selAreas = filters?.areas || [];
  const selCoords = filters?.coordenadores || [];
  if (selAreas.length && !selAreas.includes(area.nome)) return false;
  if (!selCoords.length) return true;
  if (area.coordenadorId == null) return false;
  const coord = coordenadores.find((c) => c.id === area.coordenadorId);
  return !!coord && selCoords.includes(coord.nome);
}

/**
 * Busca da toolbar em /cadastros/equipe: área (nome/sigla), técnicos da área e coordenador.
 * @param {string} searchLower já trim + toLowerCase; vazio = tudo visível
 */
export function areaMatchesBoardSearch(area, searchLower, tecnicos, coordenadores) {
  if (!searchLower) return true;
  if (!area) return false;
  const nome = String(area.nome || '').toLowerCase();
  const abbr = String(area.abreviacao || '').toLowerCase();
  if (nome.includes(searchLower) || abbr.includes(searchLower)) return true;

  const areaId = area.id;
  if (
    areaId != null
    && (tecnicos || []).some(
      (t) => t.areaId === areaId && String(t.nome || '').toLowerCase().includes(searchLower),
    )
  ) {
    return true;
  }

  if (area.coordenadorId == null) return false;
  const coord = (coordenadores || []).find((c) => c.id === area.coordenadorId);
  return !!coord && String(coord.nome || '').toLowerCase().includes(searchLower);
}
